// src/EstimateModalInfinity.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

const BLUE = "#1E90FF";

const backdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

const panel = {
  hidden: { opacity: 0, y: 18, scale: 0.985, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.35, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: 10,
    scale: 0.985,
    filter: "blur(8px)",
    transition: { duration: 0.22, ease: "easeIn" },
  },
};

const stepSwap = {
  hidden: { opacity: 0, y: 10, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.35, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -8,
    filter: "blur(8px)",
    transition: { duration: 0.22, ease: "easeIn" },
  },
};

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] uppercase tracking-[0.16em] text-white/60">
        {label} {required && <span style={{ color: BLUE }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const Input = React.forwardRef(function Input(props, ref) {
  return (
    <input
      ref={ref}
      {...props}
      className="w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:border transition"
      style={{
        boxShadow: "none",
      }}
    />
  );
});

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 transition"
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0B1020]">
          {o}
        </option>
      ))}
    </select>
  );
}

function StepPill({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={[
          "h-6 w-6 rounded-full border flex items-center justify-center text-[11px] font-mono",
          active
            ? "text-white"
            : done
            ? "border-white/20 bg-white/10 text-white/80"
            : "border-white/15 bg-white/5 text-white/55",
        ].join(" ")}
        style={
          active
            ? {
                borderColor: `${BLUE}99`,
                backgroundColor: BLUE,
                boxShadow: `0 0 18px rgba(30,144,255,.45)`,
              }
            : {}
        }
      >
        {n}
      </div>
      <span
        className={[
          "text-[11px] uppercase tracking-[0.18em]",
          active || done ? "text-white/80" : "text-white/45",
        ].join(" ")}
      >
        {label}
      </span>
    </div>
  );
}

export function EstimateModalInfinity({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const SERVICES = useMemo(
    () => [
      "Plumbing Repair",
      "AC Repair",
      "AC Installation",
      "Heating Repair",
      "Water Heater Install",
      "Indoor Air Quality",
      "Emergency Service",
      "Other",
    ],
    []
  );

  const initial = useMemo(
    () => ({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      serviceType: "Plumbing Repair",
      notes: "",
      consent: false,
    }),
    []
  );

  const [form, setForm] = useState(initial);
  const set = (k) => (e) =>
    setForm((p) => ({
      ...p,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const close = () => {
    if (loading) return;
    setStep(1);
    setSent(false);
    setError("");
    setForm(initial);
    onClose?.();
  };

  const canNext =
    (step === 1 &&
      form.firstName.length > 1 &&
      form.lastName.length > 1 &&
      form.phone.length > 6 &&
      form.email.includes("@")) ||
    (step === 2 && form.serviceType) ||
    (step === 3 && form.consent);

  const next = () => canNext && setStep((s) => s + 1);
  const back = () => (step === 1 ? close() : setStep((s) => s - 1));

  const submit = async () => {
    if (!form.consent) {
      setError("Please check consent.");
      return;
    }

    setSent(true);

    try {
      await addDoc(collection(db, "infinityLeads"), {
        ...form,
        fullName: `${form.firstName} ${form.lastName}`,
        createdAt: serverTimestamp(),
        source: "infinityairwater.com",
      });
    } catch (err) {
      console.warn(err);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          initial="hidden"
          animate="show"
          exit="exit"
          variants={backdrop}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={close} />

          <motion.div
            variants={panel}
            className="relative w-full max-w-xl rounded-3xl border border-white/10 bg-[#0B1020] shadow-[0_30px_120px_rgba(0,0,0,0.75)] overflow-hidden"
          >
            <div className="pointer-events-none absolute -bottom-28 -right-24 h-80 w-80 rounded-full blur-[110px]" style={{ background: `${BLUE}33` }} />

            <div className="border-b border-white/10 px-6 py-5">
              <h3 className="text-lg font-semibold text-white">Schedule Service</h3>
              <div className="mt-4 flex gap-4">
                <StepPill n={1} label="Contact" active={step === 1} done={step > 1} />
                <StepPill n={2} label="Service" active={step === 2} done={step > 2} />
                <StepPill n={3} label="Confirm" active={step === 3} done={false} />
              </div>
            </div>

            <div className="px-6 py-6">
              {sent ? (
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border"
                       style={{ background: `${BLUE}22`, borderColor: `${BLUE}66` }}>
                    ✓
                  </div>
                  <h4 className="text-lg font-semibold text-white">You're Booked</h4>
                  <p className="mt-2 text-sm text-white/70">
                    Our HVAC & Plumbing team will contact you shortly.
                  </p>
                </div>
              ) : (
                <>
                  {step === 1 && (
                    <div className="space-y-4">
                      <Input placeholder="First Name" value={form.firstName} onChange={set("firstName")} />
                      <Input placeholder="Last Name" value={form.lastName} onChange={set("lastName")} />
                      <Input placeholder="Phone" value={form.phone} onChange={set("phone")} />
                      <Input placeholder="Email" value={form.email} onChange={set("email")} />
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <Select value={form.serviceType} onChange={set("serviceType")} options={SERVICES} />
                      <textarea
                        rows={4}
                        placeholder="Describe the issue..."
                        value={form.notes}
                        onChange={set("notes")}
                        className="w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white"
                      />
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4">
                      <label className="flex gap-3 text-xs text-white/70">
                        <input type="checkbox" checked={form.consent} onChange={set("consent")} />
                        I consent to receive service-related communication.
                      </label>
                    </div>
                  )}

                  <div className="mt-6 flex justify-between">
                    <button onClick={back} className="text-white/60 text-sm">
                      {step === 1 ? "Cancel" : "Back"}
                    </button>

                    {step < 3 ? (
                      <button
                        onClick={next}
                        disabled={!canNext}
                        className="rounded-full px-5 py-2.5 text-xs font-semibold text-white"
                        style={{ background: BLUE }}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={submit}
                        className="rounded-full px-5 py-2.5 text-xs font-semibold text-white"
                        style={{ background: BLUE }}
                      >
                        Schedule Service →
                      </button>
                    )}
                  </div>

                  {error && (
                    <div className="mt-4 text-red-300 text-xs">{error}</div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}